import { useState, useCallback } from 'react';
import { GeolocationService, UserLocation, GeolocationError, GeolocationErrorType } from '../services/GeolocationService';
import { useI18n } from '../i18n/I18nContext';

interface UseGeolocationReturn {
  userLocation: UserLocation | null;
  isLocating: boolean;
  locationError: string;
  getCurrentLocation: () => Promise<void>;
}

export const useGeolocation = (): UseGeolocationReturn => {
  const { t } = useI18n();
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState('');

  const getCurrentLocation = useCallback(async () => {
    setIsLocating(true);
    setLocationError('');

    // Log environment info for TWA debugging
    const envInfo = {
      userAgent: navigator.userAgent,
      standalone: window.matchMedia('(display-mode: standalone)').matches,
      fullscreen: window.matchMedia('(display-mode: fullscreen)').matches,
      navigatorStandalone: (window.navigator as any).standalone,
      referrer: document.referrer,
      hasGeolocation: !!navigator.geolocation,
      buildVersion: '331-fix'
    };
    console.log('[useGeolocation] Environment check:', envInfo);
    
    // Temporary debug: show version to confirm new code is loaded
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('[useGeolocation] TWA MODE DETECTED - Will force high accuracy');
    }

    try {
      const location = await GeolocationService.getCurrentLocation();
      setUserLocation(location);
      setIsLocating(false);
      setLocationError('');
    } catch (error) {
      setIsLocating(false);
      
      // Translate the error message based on error type
      let translatedError = t('map.locationError');
      let debugInfo = '';
      
      if (error instanceof GeolocationError) {
        switch (error.errorType) {
          case GeolocationErrorType.PERMISSION_DENIED:
            translatedError = t('map.locationDenied');
            break;
          case GeolocationErrorType.POSITION_UNAVAILABLE:
            translatedError = t('map.locationUnavailable');
            break;
          case GeolocationErrorType.TIMEOUT:
            translatedError = t('map.locationTimeout');
            break;
          default:
            translatedError = t('map.locationError');
            break;
        }
        // Add technical details for debugging - include full error message
        debugInfo = ` [${error.errorType}: "${error.message}"]`;
        
        // Special handling for NoTwaFound error
        if (error.message.includes('NoTwaFound')) {
          debugInfo += ' | TWA not verified by Play Store';
        }
      } else {
        // Generic error
        const errorMsg = error instanceof Error ? error.message : String(error);
        debugInfo = ` [ERROR: ${errorMsg}]`;
        if (errorMsg.includes('NoTwaFound')) {
          debugInfo += ' | Install from Play Store for location delegation';
        }
      }
      
      setLocationError(translatedError + debugInfo);

      // Clear location error after 8 seconds (longer to allow user to read instructions)
      setTimeout(() => {
        setLocationError('');
      }, 8000);
    }
  }, [t]);

  return {
    userLocation,
    isLocating,
    locationError,
    getCurrentLocation,
  };
};
